(function () {
  const { gsap, MorphSVGPlugin } = window;

  // Bail gracefully if GSAP failed to load (CDN down) — don't lock the homepage
  if (!gsap) {
    const el = document.getElementById('gg-page-loader');
    if (el) el.style.display = 'none';
    return;
  }

  gsap.registerPlugin(MorphSVGPlugin);

  const loader = document.getElementById('gg-page-loader');
  const svg    = document.getElementById('gg-loader-svg');
  const path   = document.getElementById('gg-loader-path');

  // Bail if DOM elements were removed by a third-party app or theme editor
  if (!loader || !svg || !path) return;

  const origD = path.getAttribute('d') ?? '';

  /** @type {any} */
  let pulseTl = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let navTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let safetyTimer;
  let loaderShown = false;

  // Set once — GSAP persists transformOrigin, no need to repeat it on each tween
  gsap.set(svg, { scale: 0, opacity: 0, transformOrigin: '50% 50%' });

  function enter(onDone) {
    loaderShown = true;
    gsap.timeline({ onComplete: onDone })
      .to(loader, { opacity: 1, duration: 0.2, ease: 'none' })
      .to(svg, { scale: 1, opacity: 1, duration: 0.42, ease: 'power3.out' })
      .to(svg, { rotation: 720, duration: 0.36, ease: 'circ.in' })
      .to(path, { morphSVG: '#gg-loader-dot', duration: 0.38, ease: 'power1.inOut' }, '<+=0.08')
      .add(() => {
        pulseTl = gsap.to(svg, {
          scale: 1.5, duration: 0.65, ease: 'sine.inOut', yoyo: true, repeat: -1,
        });
      });
  }

  function exit() {
    if (pulseTl) { pulseTl.kill(); pulseTl = null; }
    gsap.killTweensOf([svg, path]);

    gsap.timeline({ onComplete: () => gsap.set(loader, { display: 'none' }) })
      .to(svg,   { scale: 0, duration: 0.22, ease: 'power3.in' })
      .set(path, { morphSVG: origD })
      .set(svg,  { rotation: 0, opacity: 1 })
      .to(svg,   { scale: 1, duration: 0.42, ease: 'power3.out' })
      .to(loader, { opacity: 0, duration: 0.3, ease: 'power1.in' });
  }

  // Used when arriving via navigation — overlay already visible from CSS, just fade out.
  // No crown animation; the user already saw it on the page they came from.
  function exitFast() {
    gsap.to(loader, { opacity: 0, duration: 0.3, ease: 'power1.in',
      onComplete: () => gsap.set(loader, { display: 'none' }) });
  }

  function showForNavigation() {
    navTimer = undefined;
    sessionStorage.setItem('gg-nav-loading', '1');
    if (pulseTl) { pulseTl.kill(); pulseTl = null; }
    gsap.killTweensOf([loader, svg, path]);
    gsap.set(loader, { display: 'flex', opacity: 0 });
    gsap.set(svg,  { scale: 0, opacity: 0, rotation: 0 });
    gsap.set(path, { morphSVG: origD });
    enter();
    // Safety: if navigation is cancelled (no pagehide), bail after 8s so the
    // loader doesn't run forever and the stale sessionStorage key gets cleared
    safetyTimer = setTimeout(() => {
      sessionStorage.removeItem('gg-nav-loading');
      exit();
    }, 8000);
  }

  // Delay 300ms before showing — same threshold as initial page load.
  // If the page unloads within that window, pagehide clears the timer and nothing shows.
  document.addEventListener('click', function (e) {
    const a = e.target.closest('a[href]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('javascript:') || a.target === '_blank') return;
    try { if (new URL(a.href).origin !== location.origin) return; } catch (_) { return; }

    requestAnimationFrame(() => {
      if (e.defaultPrevented) return;
      clearTimeout(navTimer);
      navTimer = setTimeout(showForNavigation, 300);
    });
  }, true);

  // Fast page: unloads before 300ms — cancel the pending show
  window.addEventListener('pagehide', () => {
    clearTimeout(navTimer);
    clearTimeout(safetyTimer);
    navTimer = undefined;
    safetyTimer = undefined;
  });

  // Restored from bfcache (browser back/forward) — reset any stale loader state
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      clearTimeout(navTimer);
      clearTimeout(safetyTimer);
      navTimer = undefined;
      safetyTimer = undefined;
      loaderShown = false;
      if (pulseTl) { pulseTl.kill(); pulseTl = null; }
      gsap.set(loader, { display: 'none', opacity: 0 });
    }
  });

  window.__ggLoader = {
    preview() {
      clearTimeout(navTimer);
      clearTimeout(safetyTimer);
      navTimer = undefined;
      safetyTimer = undefined;
      loaderShown = false;
      if (pulseTl) { pulseTl.kill(); pulseTl = null; }
      gsap.killTweensOf([loader, svg, path]);
      gsap.set(loader, { opacity: 0, display: 'flex' });
      gsap.set(svg,    { scale: 0, opacity: 0, rotation: 0 });
      gsap.set(path,   { morphSVG: origD });
      enter(() => setTimeout(exit, 900));
    },
  };

  if (loader.hasAttribute('data-always-show')) {
    // Homepage — always play full animation, hold briefly after load, then exit
    enter();
    window.addEventListener('load', () => setTimeout(exit, 1200), { once: true });

  } else if (loader.hasAttribute('data-nav-show')) {
    // Arrived via a slow navigation — overlay is already covering the page via CSS.
    // No re-animation; just wait for assets and fade out.
    // Note: data-always-show is checked first, so navigating to the homepage runs the
    // full animation even if data-nav-show is also present.
    if (document.readyState === 'complete') {
      exitFast();
    } else {
      window.addEventListener('load', exitFast, { once: true });
    }

  } else if (document.readyState === 'complete') {
    // Page loaded before defer scripts ran — hide immediately, nothing to show
    gsap.set(loader, { display: 'none' });

  } else {
    // Slow page: wait 300ms before showing
    const showTimer = setTimeout(() => enter(), 300);
    window.addEventListener('load', () => {
      clearTimeout(showTimer);
      // Only run exit() if enter() actually started — avoids a GSAP timeline on
      // invisible elements when the page loaded within the 300ms window
      if (loaderShown) exit(); else gsap.set(loader, { display: 'none' });
    }, { once: true });
  }
})();
